import {
  Bell, BellOff, ExternalLink, FolderTree, Image, LayoutDashboard, LogOut, Mail, Menu,
  MessageSquareQuote, Package, Palette, Paintbrush, Settings, ShoppingCart, FileText, HelpCircle, Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useSite } from "@/context/SiteContext";
import { playNotificationSound, useOrderNotifier, ensureNotificationPermission } from "@/hooks/useOrderNotifier";
import { API } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart, id: "orders" },
  { to: "/admin/products", label: "Products", icon: Package, id: "products" },
  { to: "/admin/categories", label: "Categories", icon: FolderTree, id: "categories" },
  { to: "/admin/gallery", label: "Gallery", icon: Image, id: "gallery" },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote, id: "testimonials" },
  { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, id: "faqs" },
  { to: "/admin/content", label: "Website Content", icon: FileText, id: "content" },
  { to: "/admin/landing-editor", label: "Landing Editor", icon: Paintbrush, id: "landing-editor" },
  { to: "/admin/branding", label: "Branding & Logo", icon: Palette, id: "branding" },
  { to: "/admin/enquiries", label: "Enquiries", icon: Mail, id: "enquiries" },
  { to: "/admin/admins", label: "Admins", icon: Users, id: "admins" },
  { to: "/admin/settings", label: "Settings", icon: Settings, id: "settings" },
];

function NavItems({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1" aria-label="Admin">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          data-testid={`admin-nav-${n.id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-foreground/75 hover:bg-muted"
            }`
          }
        >
          <n.icon className="h-4 w-4 shrink-0" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const authed = !!user && user !== false && !user.must_change_password;
  const { settings } = useSite();
  const { soundOn, setSoundOn } = useOrderNotifier(authed, settings?.notification);

  useEffect(() => {
    API.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    try {
      await API.post("/auth/logout");
    } catch {}
    navigate("/admin/login", { replace: true });
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (user.must_change_password) return <Navigate to="/admin/set-password" replace />;

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-layout">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r bg-card p-4 z-30">
        <p className="font-display text-lg text-primary px-2 py-4">Eileen Jenkins Florist</p>
        <NavItems />
        <div className="mt-auto space-y-1 pt-4 border-t">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground/75 hover:bg-muted" data-testid="admin-view-site-link">
            <ExternalLink className="h-4 w-4" /> View website
          </a>
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground/75 hover:bg-muted" data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-14 border-b bg-background/85 backdrop-blur-xl flex items-center justify-between px-4" data-testid="admin-topbar">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="admin-mobile-menu-btn" aria-label="Menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72" data-testid="admin-mobile-menu">
                <p className="font-display text-lg text-primary px-2 py-4">Admin Panel</p>
                <NavItems onNavigate={() => setOpen(false)} />
                <div className="mt-6 pt-4 border-t space-y-1">
                  <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm hover:bg-muted">
                    <ExternalLink className="h-4 w-4" /> View website
                  </a>
                  <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm hover:bg-muted" data-testid="admin-logout-btn-mobile">
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              </SheetContent>
            </Sheet>
            <p className="text-sm text-muted-foreground hidden sm:block">Signed in as <span className="font-medium text-foreground">{user.email}</span></p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="icon"
              onClick={() => {
                const next = !soundOn;
                setSoundOn(next);
                ensureNotificationPermission();
                if (next) playNotificationSound(settings?.notification);
              }}
              title={soundOn ? "Order notification sound: on" : "Order notification sound: off"}
              aria-label="Toggle order notification sound"
              data-testid="admin-sound-toggle"
            >
              {soundOn ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4 text-muted-foreground" />}
            </Button>
            <ThemeToggle />
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
